import React, { useState, useEffect, useContext, useRef } from 'react';
import { format } from 'date-fns';
import * as Yup from 'yup';
import { Formik, FieldArray, Form, Field } from 'formik';
import { toast } from 'react-toastify';

import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  CircularProgress,
  useTheme,
} from '@mui/material';

import { DeleteOutline } from '@mui/icons-material';

import { TagsContainer } from '../TagsContainer';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastError from '../../toasts/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getStyled } from './styles';

const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Too Short!')
    .max(250, 'Too Long!')
    .required('Required'),
  number: Yup.string().min(8, 'Too Short!').max(50, 'Too Long!'),
  email: Yup.string().email('Invalid email'),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
  const theme = useTheme();
  const classes = getStyled(theme);
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const initialState = {
    name: '',
    number: '',
    email: '',
    disableBot: false,
    lgpdAcceptedAt: '',
  };

  const [contact, setContact] = useState(initialState);
  const [disableBot, setDisableBot] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        setContact((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      if (!contactId) return;

      try {
        const { data } = await api.get(`/contacts/${contactId}`);
        if (isMounted.current) {
          setContact(data);
          setDisableBot(data.disableBot);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchContact();
  }, [contactId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setContact(initialState);
  };

  const handleSaveContact = async (values) => {
    try {
      let contactData;
      if (contactId) {
        await api.put(`/contacts/${contactId}`, {
          ...values,
          disableBot: disableBot,
        });
        handleClose();
      } else {
        const { data: contact } = await api.post('/contacts', {
          ...values,
          disableBot: disableBot,
        });
        if (onSave) {
          onSave(contact);
          contactData = contact;
        }
      }
      toastSuccess(i18n.t('contactModal.success'));

      try {
        const { data: ticket } = await api.post('/tickets', {
          contactId: contactData.id,
          whatsappId: user.whatsappId,
          queueId: user.queueId,
          userId: user.id,
          status: 'open',
        });

        if (ticket !== undefined && ticket.uuid !== undefined) {
          navigate(`/tickets/${ticket.uuid}`);
        }
      } catch (err) {
        const showError = (err) => {
          const formatted = (
            err?.response?.data?.error ||
            err.message ||
            'Erro desconhecido'
          ).replace(/\n/g, '<br />');

          toastError(<div dangerouslySetInnerHTML={{ __html: formatted }} />);
        };
        showError(err);
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div style={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth='lg' scroll='paper'>
        <DialogTitle id='form-dialog-title' sx={theme.custom.dialogColor}>
          {contactId
            ? `${i18n.t('contactModal.title.edit')}`
            : `${i18n.t('contactModal.title.add')}`}
        </DialogTitle>
        <Formik
          initialValues={contact}
          enableReinitialize={true}
          validationSchema={ContactSchema}
          onSubmit={(values, actions) => {
            try {
              handleSaveContact(values);
            } finally {
              actions.setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched, isSubmitting }) => (
            <Form>
              <DialogContent dividers sx={theme.custom.dialogColor}>
                <Typography variant='subtitle1' gutterBottom>
                  {i18n.t('contactModal.form.mainInfo')}
                </Typography>
                <Field
                  as={TextField}
                  label={i18n.t('contactModal.form.name')}
                  name='name'
                  autoFocus
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant='outlined'
                  margin='dense'
                  sx={theme.custom.textField}
                />
                <Field
                  as={TextField}
                  label={i18n.t('contactModal.form.number')}
                  name='number'
                  error={touched.number && Boolean(errors.number)}
                  helperText={touched.number && errors.number}
                  placeholder='5544912344321'
                  variant='outlined'
                  margin='dense'
                  sx={theme.custom.textField}
                />
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t('contactModal.form.email')}
                    name='email'
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    placeholder='Email address'
                    fullWidth
                    margin='dense'
                    variant='outlined'
                    sx={theme.custom.textField}
                  />
                </div>
                <div>
                  <TagsContainer contact={contact} />
                </div>
                <Typography
                  sx={{
                    marginBottom: 6,
                    marginTop: 3,
                    color: theme.custom.colors.text,
                  }}
                  variant='subtitle1'
                >
                  {i18n.t('contactModal.form.whatsapp')}{' '}
                  {contact?.whatsapp ? contact?.whatsapp.name : ''}
                </Typography>
                <Typography
                  sx={{
                    marginBottom: 6,
                    marginTop: 3,
                    color: theme.custom.colors.text,
                  }}
                  variant='subtitle1'
                >
                  {i18n.t('contactModal.form.termsLGDP')}{' '}
                  {contact?.lgpdAcceptedAt
                    ? format(
                        new Date(contact?.lgpdAcceptedAt),
                        "dd/MM/yyyy 'às' HH:mm",
                      )
                    : ''}
                </Typography>
                <Typography
                  sx={{
                    marginBottom: 6,
                    marginTop: 3,
                    color: theme.custom.colors.text,
                  }}
                  variant='subtitle1'
                >
                  {i18n.t('contactModal.form.extraInfo')}
                </Typography>

                <FieldArray name='extraInfo'>
                  {({ push, remove }) => (
                    <>
                      {values.extraInfo &&
                        values.extraInfo.length > 0 &&
                        values.extraInfo.map((info, index) => (
                          <div style={classes.extraAttr} key={`${index}-info`}>
                            <Field
                              as={TextField}
                              label={i18n.t('contactModal.form.extraName')}
                              name={`extraInfo[${index}].name`}
                              variant='outlined'
                              margin='dense'
                              style={theme.custom.textField}
                            />
                            <Field
                              as={TextField}
                              label={i18n.t('contactModal.form.extraValue')}
                              name={`extraInfo[${index}].value`}
                              variant='outlined'
                              margin='dense'
                              style={theme.custom.textField}
                            />
                            <IconButton
                              size='small'
                              onClick={() => remove(index)}
                            >
                              <DeleteOutline />
                            </IconButton>
                          </div>
                        ))}
                      <div style={classes.extraAttr}>
                        <Button
                          sx={theme.custom.editButton}
                          variant='outlined'
                          onClick={() => push({ name: '', value: '' })}
                        >
                          {`+ ${i18n.t('contactModal.buttons.addExtraInfo')}`}
                        </Button>
                      </div>
                    </>
                  )}
                </FieldArray>
              </DialogContent>
              <DialogActions sx={theme.custom.dialogColor}>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  variant='outlined'
                  sx={theme.custom.cancelButton}
                >
                  {i18n.t('contactModal.buttons.cancel')}
                </Button>
                <Button
                  type='submit'
                  disabled={isSubmitting}
                  variant='outlined'
                  sx={theme.custom.confirmButton}
                >
                  {contactId
                    ? `${i18n.t('contactModal.buttons.okEdit')}`
                    : `${i18n.t('contactModal.buttons.okAdd')}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      style={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ContactModal;
